/**
 * Share Live Captions on Your Network (feature guide)
 *
 * Screenshots were taken from Sokuji's main branch (e2c28b61), the build that
 * first carries LAN caption sharing; it follows v0.42.3.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Share Live Captions on Your Network",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "The Sokuji desktop app can share its live captions and translations with people on the same Wi‑Fi or office network. They scan a QR code or type an address, and read along on their own phone or laptop in the language they choose. There is nothing to install and no sign-in."
  },
  "steps": [
    {
      "title": "Open the share panel",
      "content": "In the desktop app, click the cast button in the toolbar above the conversation (its tooltip says <strong>Share captions</strong>). Nothing is shared until you switch it on.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "Turn on sharing",
      "content": "Switch on <strong>Share captions</strong>. The panel shows a QR code and an address such as <code>http://192.168.1.23:7788/</code>, and <strong>Watching</strong> counts the devices that have the page open. The first time, Windows may ask whether Sokuji can use the network: allow it on private networks. If your computer has more than one network connection, a <strong>Network</strong> list appears; choose the one your viewers are on.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "Viewers open the page",
      "content": "People on the same network scan the QR code with their phone camera, or type the address into a browser. <strong>Copy</strong> copies the address so you can send it in a chat. They choose what to read (the original language, the translation, or both) and tap <strong>Start reading</strong>. Someone who joins late also gets what was said before.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "Read on a laptop",
      "content": "On a wide screen, choosing <strong>Both languages</strong> puts the two languages side by side, with the time of each line. Click the selected button again to swap which language comes first. Keys: <code>+</code> and <code>−</code> change the text size, <code>B</code> switches both languages on or off, <code>F</code> goes full screen, and <code>End</code> returns to the newest line.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "Viewers adjust their own page",
      "content": "Under <strong>Display</strong> (the <strong>⋯</strong> button on a laptop), each viewer can change the text size, pick <strong>Light</strong> or <strong>High contrast</strong> colors, hide the sentence still being spoken with <strong>Only complete sentences</strong>, and use <strong>Keep the screen on</strong>. These choices apply to that device only. If you switch on <strong>Let viewers save these captions</strong> in the share panel, viewers also get <strong>Save these captions</strong>, which downloads the captions on their page as a .txt file.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "Show the QR code on a big screen",
      "content": "Click <strong>Open projector page</strong>. A separate window opens with the QR code and the address in large type; drag it to a projector or a second display and make it full screen. To help guests get online first, switch on <strong>Add a Wi‑Fi hint to the projector page</strong> and enter the <strong>Wi‑Fi name</strong> and the password. The page then shows them, with a second QR code that joins the Wi‑Fi. Sokuji uses these details only on this page and does not save them.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "Stop sharing",
      "content": "Click <strong>Stop sharing</strong>, or quit Sokuji. The viewers’ pages show <strong>Sharing has ended</strong>. While sharing is on, their pages follow your conversation: when you clear it or start a new session, their pages start again from empty. Sharing is always off when Sokuji starts."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "Everyone must be on the same network as your computer. A phone on mobile data cannot open the page.",
      "Guest Wi‑Fi and some company networks stop devices from reaching each other. If nobody can connect, use a phone hotspot or your own router. Sokuji warns you when no device has connected after two minutes.",
      "Android Chrome won't open it? Allow it in Settings → Apps → Chrome → Permissions → Nearby devices. The projector page shows this reminder too.",
      "The address starts with http://, so a browser may call the page “not secure”. It is an address inside your own network.",
      "The page has no password: anyone on the network who has the address can read the captions. Turn sharing off when the conversation is not meant for everyone on that network.",
      "If your computer changes network, the address changes. Sokuji tells you; send the new address or open the projector page again.",
      "When you also translate the other side of a call, each line is marked On site or Remote. When the other side has two or more speakers (Soniox, with your own key or through KizunaAI), their lines are marked Speaker 1, Speaker 2 and so on.",
      "In a face-to-face conversation, the other person can follow along on their own phone instead of your screen."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Do viewers need Sokuji or an account?",
        "answer": "No. A current browser on a phone, tablet or computer is enough."
      },
      {
        "question": "Does it work over the internet?",
        "answer": "No. Viewers must be on the same Wi‑Fi or wired network as your computer."
      },
      {
        "question": "Do the captions pass through Kizuna AI’s servers?",
        "answer": "No. Sokuji serves the page from your own computer, and the captions go straight to the viewers’ devices over your network. The translation itself still comes from the provider you use, as in any session."
      },
      {
        "question": "Does sharing cost more?",
        "answer": "No. Viewers read the captions your session already produces, however many of them there are."
      },
      {
        "question": "Can viewers hear the translation?",
        "answer": "No. The page shows text only."
      },
      {
        "question": "Is it in the browser extension?",
        "answer": "No. Caption sharing is in the desktop app for Windows, macOS and Linux."
      },
      {
        "question": "Why did the address stop working?",
        "answer": "The address belongs to your computer on its current network. It changes when you switch networks, and sharing is off again each time Sokuji starts. Turn sharing on and share the new address."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "在局域网内分享实时字幕",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "Sokuji 桌面应用可以把实时字幕和译文分享给同一个 Wi‑Fi 或办公网络里的人。他们扫描二维码或输入地址，就能在自己的手机或电脑上用自己选择的语言同步阅读，不用安装任何东西，也不用登录。"
  },
  "steps": [
    {
      "title": "打开分享面板",
      "content": "在桌面应用中，点击对话上方工具栏里的投屏按钮（鼠标悬停时显示<strong>分享字幕</strong>）。在你开启之前，不会分享任何内容。",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "开启分享",
      "content": "把<strong>开启分享</strong>开关打开。面板会显示二维码和一个类似 <code>http://192.168.1.23:7788/</code> 的地址，<strong>在看</strong>显示已打开页面的设备数量。第一次开启时，Windows 可能会询问是否允许 Sokuji 访问网络：请允许它访问专用网络。如果你的电脑有多个网络连接，会出现<strong>网络</strong>列表，请选择观众所在的那个网络。",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "观众打开页面",
      "content": "同一网络里的人用手机相机扫描二维码，或在浏览器里输入地址。点<strong>复制</strong>可以复制地址，方便你发到聊天里。他们选择要看的内容（原文、译文或双语），然后点<strong>开始看</strong>。晚加入的人也能看到之前说过的内容。",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "在电脑上阅读",
      "content": "在宽屏上选择<strong>双语</strong>时，两种语言左右并排显示，并带有每一行的时间。再点一次已选中的这个按钮，可以交换哪种语言在前。快捷键：<code>+</code> 和 <code>−</code> 调整字号，<code>B</code> 开关双语，<code>F</code> 全屏，<code>End</code> 回到最新一行。",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "观众调整自己的页面",
      "content": "在<strong>显示设置</strong>里（电脑上是 <strong>⋯</strong> 按钮），每位观众可以调整字号，选择<strong>浅色</strong>或<strong>高对比</strong>配色，用<strong>只显示完整句</strong>隐藏还没说完的句子，以及使用<strong>保持屏幕常亮</strong>。这些选项只对那台设备生效。如果你在分享面板里打开<strong>允许听众保存本场字幕</strong>，观众还会看到<strong>保存本场字幕</strong>，可以把自己页面上的字幕下载为 .txt 文件。",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "把二维码放到大屏幕上",
      "content": "点击<strong>打开投屏页</strong>。Sokuji 会打开一个单独的窗口，用大字显示二维码和地址；把它拖到投影仪或第二块屏幕上并全屏即可。如果想先帮来宾连上网络，打开<strong>投屏页加 Wi‑Fi 提示</strong>，填写<strong>Wi‑Fi 名称</strong>和密码。页面会显示这些信息，并多出一个用来连接 Wi‑Fi 的二维码。Sokuji 只在这个页面上使用这些信息，不会保存。",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "结束分享",
      "content": "点击<strong>结束分享</strong>，或退出 Sokuji。观众的页面会显示<strong>分享已结束</strong>。分享期间，观众的页面与你的对话保持一致：你清空对话或开始新的会话时，他们的页面也会清空重新开始。每次启动 Sokuji 时，分享都是关闭的。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "所有人都必须和你的电脑在同一个网络里。使用移动数据的手机打不开这个页面。",
      "访客 Wi‑Fi 和部分公司网络会阻止设备之间互相访问。如果没有人能连上，可以改用手机热点或自带的路由器。两分钟内没有设备连上时，Sokuji 会提醒你。",
      "安卓 Chrome 打不开？在「设置 → 应用 → Chrome → 权限 → 附近设备」里允许。投屏页上也有这条提醒。",
      "地址以 http:// 开头，所以浏览器可能提示页面“不安全”。这是你自己网络内部的地址。",
      "页面没有密码：同一网络里知道地址的人都能看到字幕。对话内容不适合让这个网络里的所有人看到时，请关闭分享。",
      "电脑更换网络后，地址也会改变。Sokuji 会提示你；请重新发送新地址，或重新打开投屏页。",
      "如果你同时翻译通话中对方的声音，每一行会标注现场或线上。当对方有两个或更多说话人时（Soniox，使用自己的密钥或通过 KizunaAI），他们的发言会标注说话人 1、说话人 2 等。",
      "面对面对话时，对方可以用自己的手机跟着看，而不必看你的屏幕。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "观众需要安装 Sokuji 或注册账号吗？",
        "answer": "不需要。手机、平板或电脑上有较新的浏览器就可以。"
      },
      {
        "question": "可以通过互联网使用吗？",
        "answer": "不可以。观众必须和你的电脑连接同一个 Wi‑Fi 或有线网络。"
      },
      {
        "question": "字幕会经过 Kizuna AI 的服务器吗？",
        "answer": "不会。页面由 Sokuji 在你自己的电脑上提供，字幕通过你的网络直接发送到观众的设备。翻译本身仍然来自你所使用的服务商，和平时的会话一样。"
      },
      {
        "question": "分享会产生额外费用吗？",
        "answer": "不会。观众看到的是你的会话本来就生成的字幕，观众再多也不会多收费。"
      },
      {
        "question": "观众能听到翻译的语音吗？",
        "answer": "不能。页面只显示文字。"
      },
      {
        "question": "浏览器扩展里有这个功能吗？",
        "answer": "没有。字幕分享只在 Windows、macOS 和 Linux 的桌面应用中提供。"
      },
      {
        "question": "为什么地址打不开了？",
        "answer": "地址对应的是你的电脑在当前网络中的位置。切换网络后地址会改变，而且每次启动 Sokuji 时分享都是关闭的。请重新开启分享，并把新地址发给观众。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "同じネットワーク内でリアルタイム字幕を共有する",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "Sokuji デスクトップアプリは、リアルタイムの字幕と訳文を、同じ Wi‑Fi やオフィスのネットワークにいる人と共有できます。相手は QR コードを読み取るかアドレスを入力するだけで、自分のスマートフォンやノートパソコンで、選んだ言語の字幕を読めます。インストールもサインインも不要です。"
  },
  "steps": [
    {
      "title": "共有パネルを開く",
      "content": "デスクトップアプリで、会話の上のツールバーにあるキャストボタンをクリックします（カーソルを合わせると<strong>字幕を共有</strong>と表示されます）。オンにするまでは、何も共有されません。",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "共有をオンにする",
      "content": "<strong>字幕を共有する</strong>をオンにします。パネルに QR コードと <code>http://192.168.1.23:7788/</code> のようなアドレスが表示され、<strong>閲覧中</strong>にはページを開いている端末の数が表示されます。初回は、Sokuji にネットワークの利用を許可するかどうかを Windows が確認することがあります。その場合は、プライベート ネットワークでの利用を許可してください。パソコンに複数のネットワーク接続がある場合は、<strong>ネットワーク</strong>の一覧が表示されます。閲覧者がつながっているネットワークを選んでください。",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "閲覧者がページを開く",
      "content": "同じネットワークにいる人は、スマートフォンのカメラで QR コードを読み取るか、ブラウザーにアドレスを入力します。<strong>コピー</strong>を押すとアドレスがコピーされるので、チャットで送ることもできます。閲覧者は読みたいもの（原文、訳文、または両方）を選び、<strong>読み始める</strong>をタップします。途中から参加した人も、それまでの内容を読めます。",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "ノートパソコンで読む",
      "content": "幅の広い画面で<strong>両方の言語</strong>を選ぶと、2 つの言語が左右に並び、各行の時刻も表示されます。選択中のそのボタンをもう一度クリックすると、2 つの言語の順番が入れ替わります。キー操作：<code>+</code> と <code>−</code> で文字サイズを変更、<code>B</code> で両方の言語の表示をオン／オフ、<code>F</code> で全画面表示、<code>End</code> で最新の行に戻ります。",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "閲覧者が自分のページを調整する",
      "content": "<strong>表示設定</strong>（ノートパソコンでは <strong>⋯</strong> ボタン）では、閲覧者がそれぞれ文字サイズを変えたり、配色を<strong>ライト</strong>や<strong>ハイコントラスト</strong>にしたり、<strong>完成した文だけ表示</strong>で話している途中の文を隠したり、<strong>画面をつけたままにする</strong>を使ったりできます。これらの設定は、その端末にだけ適用されます。共有パネルで<strong>閲覧者にこの字幕の保存を許可する</strong>をオンにすると、閲覧者のページに<strong>この字幕を保存</strong>も表示され、そのページの字幕を .txt ファイルとしてダウンロードできます。",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "QR コードを大きな画面に映す",
      "content": "<strong>投影ページを開く</strong>をクリックします。QR コードとアドレスを大きな文字で表示する別のウィンドウが開くので、プロジェクターや 2 台目のディスプレイにドラッグして全画面表示にします。ゲストが先にネットワークへ接続できるようにするには、<strong>投影ページに Wi‑Fi の案内を表示する</strong>をオンにして、<strong>Wi‑Fi 名</strong>とパスワードを入力します。すると、その内容と、Wi‑Fi に接続するための 2 つ目の QR コードがページに表示されます。Sokuji はこの情報をこのページにだけ使い、保存はしません。",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "共有を終了する",
      "content": "<strong>共有を終了</strong>をクリックするか、Sokuji を終了します。閲覧者のページには<strong>共有は終了しました</strong>と表示されます。共有中、閲覧者のページは Sokuji の会話に連動します。会話をクリアしたり新しいセッションを始めたりすると、閲覧者のページも空の状態から始まります。Sokuji の起動時には、共有は必ずオフになっています。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "全員が、お使いのパソコンと同じネットワークにつながっている必要があります。モバイルデータ通信のスマートフォンでは、ページを開けません。",
      "ゲスト Wi‑Fi や一部の社内ネットワークでは、端末同士が通信できないようになっています。誰も接続できない場合は、スマートフォンのテザリングか自前のルーターを使ってください。2 分経っても接続した端末がないときは、Sokuji が警告を表示します。",
      "Android の Chrome で開けない場合は、「設定 → アプリ → Chrome → 権限 → 付近のデバイス」で許可してください。投影ページにも同じ案内が表示されます。",
      "アドレスは http:// で始まるため、ブラウザーがページを「安全でない」と表示することがあります。これはご自身のネットワーク内のアドレスです。",
      "ページにパスワードはありません。同じネットワークにいてアドレスを知っている人なら、誰でも字幕を読めます。そのネットワークの全員に見せる内容でない会話のときは、共有をオフにしてください。",
      "パソコンが別のネットワークに切り替わると、アドレスも変わります。Sokuji が知らせるので、新しいアドレスを送り直すか、投影ページを開き直してください。",
      "通話の相手側の音声も翻訳している場合は、各行に対面またはオンラインのラベルが付きます。相手側に 2 人以上の話者がいる場合（Soniox。自分のキーまたは KizunaAI 経由）、その行には話者 1、話者 2 などのラベルが付きます。",
      "対面での会話では、相手はあなたの画面ではなく、自分のスマートフォンで字幕を追うことができます。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "閲覧者に Sokuji やアカウントは必要ですか？",
        "answer": "いいえ。スマートフォン、タブレット、パソコンのいずれかに最近のブラウザーがあれば十分です。"
      },
      {
        "question": "インターネット経由でも使えますか？",
        "answer": "いいえ。閲覧者は、お使いのパソコンと同じ Wi‑Fi または有線ネットワークにつながっている必要があります。"
      },
      {
        "question": "字幕は Kizuna AI のサーバーを経由しますか？",
        "answer": "いいえ。ページはお使いのパソコン上の Sokuji が配信し、字幕はご自身のネットワークを通って閲覧者の端末に直接届きます。翻訳そのものは、通常のセッションと同じく、利用中のプロバイダーが行います。"
      },
      {
        "question": "共有すると追加の料金がかかりますか？",
        "answer": "いいえ。閲覧者が読むのは、セッションでもともと生成される字幕です。閲覧者が何人いても料金は変わりません。"
      },
      {
        "question": "閲覧者は翻訳の音声を聞けますか？",
        "answer": "いいえ。ページに表示されるのはテキストだけです。"
      },
      {
        "question": "ブラウザー拡張機能でも使えますか？",
        "answer": "いいえ。字幕の共有は、Windows、macOS、Linux 向けのデスクトップアプリで利用できます。"
      },
      {
        "question": "アドレスが開けなくなったのはなぜですか？",
        "answer": "アドレスは、現在のネットワーク上でのお使いのパソコンを指しています。ネットワークを切り替えるとアドレスは変わり、Sokuji を起動するたびに共有はオフに戻ります。共有をオンにして、新しいアドレスを伝えてください。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "같은 네트워크에서 실시간 자막 공유하기",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "Sokuji 데스크톱 앱은 실시간 자막과 번역을 같은 Wi‑Fi나 사무실 네트워크에 있는 사람들과 공유할 수 있습니다. 상대방은 QR 코드를 스캔하거나 주소를 입력하면 각자의 휴대폰이나 노트북에서 원하는 언어로 함께 읽을 수 있습니다. 설치할 것도 없고 로그인도 필요 없습니다."
  },
  "steps": [
    {
      "title": "공유 패널 열기",
      "content": "데스크톱 앱에서 대화 위 도구 모음에 있는 캐스트 버튼을 클릭하세요(마우스를 올리면 <strong>자막 공유</strong> 도구 설명이 나타납니다). 직접 켜기 전에는 아무것도 공유되지 않습니다.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "공유 켜기",
      "content": "<strong>자막 공유</strong> 스위치를 켜세요. 패널에 QR 코드와 <code>http://192.168.1.23:7788/</code> 같은 주소가 표시되고, <strong>시청 중</strong> 항목에는 페이지를 열어 둔 기기 수가 표시됩니다. 처음 켤 때 Windows가 Sokuji의 네트워크 사용을 허용할지 물어볼 수 있습니다. 이때 개인 네트워크에서 허용하세요. 컴퓨터에 네트워크 연결이 둘 이상 있으면 <strong>네트워크</strong> 목록이 나타납니다. 시청자가 연결된 네트워크를 선택하세요.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "시청자가 페이지 열기",
      "content": "같은 네트워크에 있는 사람은 휴대폰 카메라로 QR 코드를 스캔하거나 브라우저에 주소를 입력합니다. <strong>복사</strong> 버튼을 누르면 주소가 복사되므로 채팅으로 보낼 수도 있습니다. 시청자는 읽을 내용(원문, 번역 또는 둘 다)을 고른 뒤 <strong>보기 시작</strong> 버튼을 누릅니다. 늦게 들어온 사람도 그전에 나온 내용을 볼 수 있습니다.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "노트북에서 읽기",
      "content": "넓은 화면에서 <strong>두 언어 모두</strong> 옵션을 선택하면 두 언어가 나란히 표시되고 각 줄의 시간도 함께 나옵니다. 선택된 버튼을 한 번 더 클릭하면 어느 언어가 먼저 나올지 바뀝니다. 단축키: <code>+</code>와 <code>−</code>로 글자 크기를 조절하고, <code>B</code>로 두 언어 표시를 켜거나 끄고, <code>F</code>로 전체 화면으로 전환하고, <code>End</code>로 최신 줄로 돌아갑니다.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "시청자가 각자 페이지 조정하기",
      "content": "<strong>표시 설정</strong> 메뉴(노트북에서는 <strong>⋯</strong> 버튼)에서 시청자는 각자 글자 크기를 바꾸고, <strong>라이트</strong> 또는 <strong>고대비</strong> 색상 테마를 고르고, <strong>완성된 문장만 표시</strong> 옵션으로 아직 말하는 중인 문장을 숨기고, <strong>화면 켜짐 유지</strong> 옵션을 사용할 수 있습니다. 이 설정은 해당 기기에만 적용됩니다. 공유 패널에서 <strong>시청자가 이 자막을 저장할 수 있도록 허용</strong> 옵션을 켜면 시청자에게 <strong>이 자막 저장</strong> 항목도 나타나며, 자기 페이지의 자막을 .txt 파일로 다운로드할 수 있습니다.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "큰 화면에 QR 코드 띄우기",
      "content": "<strong>프로젝터 화면 열기</strong> 버튼을 클릭하세요. QR 코드와 주소를 큰 글자로 보여 주는 별도의 창이 열립니다. 이 창을 프로젝터나 두 번째 디스플레이로 끌어다 놓고 전체 화면으로 전환하세요. 손님이 먼저 네트워크에 접속하도록 도우려면 <strong>프로젝터 화면에 Wi‑Fi 안내 표시</strong> 옵션을 켜고 <strong>Wi‑Fi 이름</strong> 항목과 비밀번호를 입력하세요. 그러면 페이지에 이 정보와 함께 Wi‑Fi에 연결하는 두 번째 QR 코드가 표시됩니다. Sokuji는 이 정보를 이 페이지에서만 사용하며 저장하지 않습니다.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "공유 중지",
      "content": "<strong>공유 중지</strong> 버튼을 클릭하거나 Sokuji를 종료하세요. 시청자의 페이지에는 <strong>공유가 종료되었습니다</strong> 메시지가 표시됩니다. 공유하는 동안 시청자의 페이지는 내 대화를 그대로 따라갑니다. 대화를 지우거나 새 세션을 시작하면 시청자의 페이지도 빈 상태에서 다시 시작합니다. Sokuji를 시작할 때 공유는 항상 꺼져 있습니다."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "모든 사람이 내 컴퓨터와 같은 네트워크에 있어야 합니다. 모바일 데이터를 쓰는 휴대폰에서는 페이지를 열 수 없습니다.",
      "게스트 Wi‑Fi와 일부 회사 네트워크는 기기끼리 서로 연결되지 않도록 막습니다. 아무도 연결하지 못하면 휴대폰 핫스팟이나 직접 준비한 공유기를 사용하세요. 2분이 지나도 연결된 기기가 없으면 Sokuji가 알려 줍니다.",
      "Android Chrome에서 열리지 않나요? 설정 → 애플리케이션 → Chrome → 권한 → 근처 기기에서 허용하세요. 이 안내는 프로젝터 화면에도 표시됩니다.",
      "주소가 http://로 시작하므로 브라우저에 이 페이지가 안전하지 않다고 표시될 수 있습니다. 이 주소는 내 네트워크 안의 주소입니다.",
      "페이지에는 비밀번호가 없습니다. 같은 네트워크에서 주소를 아는 사람은 누구나 자막을 읽을 수 있습니다. 그 네트워크의 모든 사람에게 보여 줄 대화가 아니라면 공유를 끄세요.",
      "컴퓨터가 다른 네트워크로 바뀌면 주소도 바뀝니다. Sokuji가 알려 주니, 새 주소를 다시 보내거나 프로젝터 화면을 다시 여세요.",
      "통화 상대방 쪽도 함께 번역하는 경우 각 줄에 현장 또는 온라인 표시가 붙습니다. 상대방 쪽에 화자가 두 명 이상이면(Soniox, 내 API 키를 사용하거나 KizunaAI를 통해) 그 줄에는 화자 1, 화자 2 등의 표시가 붙습니다.",
      "대면 대화에서는 상대방이 내 화면 대신 자기 휴대폰으로 자막을 따라 볼 수 있습니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "시청자도 Sokuji나 계정이 필요한가요?",
        "answer": "아니요. 휴대폰, 태블릿 또는 컴퓨터에 최신 브라우저만 있으면 됩니다."
      },
      {
        "question": "인터넷을 통해서도 사용할 수 있나요?",
        "answer": "아니요. 시청자는 내 컴퓨터와 같은 Wi‑Fi 또는 유선 네트워크에 있어야 합니다."
      },
      {
        "question": "자막이 Kizuna AI 서버를 거치나요?",
        "answer": "아니요. Sokuji가 내 컴퓨터에서 직접 페이지를 제공하며, 자막은 내 네트워크를 통해 시청자의 기기로 바로 전송됩니다. 번역 자체는 다른 세션과 마찬가지로 사용 중인 제공업체에서 처리합니다."
      },
      {
        "question": "공유하면 비용이 더 드나요?",
        "answer": "아니요. 시청자는 세션에서 이미 만들어지는 자막을 읽을 뿐이므로, 시청자가 몇 명이든 비용은 같습니다."
      },
      {
        "question": "시청자가 번역 음성을 들을 수 있나요?",
        "answer": "아니요. 페이지에는 텍스트만 표시됩니다."
      },
      {
        "question": "브라우저 확장 프로그램에서도 쓸 수 있나요?",
        "answer": "아니요. 자막 공유는 Windows, macOS, Linux용 데스크톱 앱에서 제공됩니다."
      },
      {
        "question": "주소가 왜 열리지 않게 되었나요?",
        "answer": "주소는 현재 네트워크에서 내 컴퓨터를 가리킵니다. 네트워크를 바꾸면 주소도 바뀌고, Sokuji를 시작할 때마다 공유는 다시 꺼집니다. 공유를 켜고 새 주소를 알려 주세요."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Compartir subtítulos en tiempo real en tu red local",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "La aplicación de escritorio de Sokuji puede compartir sus subtítulos y traducciones en tiempo real con las personas que están en el mismo Wi‑Fi o en la misma red de oficina. Escanean un código QR o escriben una dirección, y van leyendo en su propio móvil u ordenador, en el idioma que elijan. No hay que instalar nada ni iniciar sesión."
  },
  "steps": [
    {
      "title": "Abre el panel para compartir",
      "content": "En la aplicación de escritorio, haz clic en el botón de transmisión de la barra de herramientas situada sobre la conversación (al pasar el ratón por encima se muestra <strong>Compartir subtítulos</strong>). No se comparte nada hasta que lo actives.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "Empieza a compartir",
      "content": "Activa <strong>Compartir subtítulos</strong>. El panel muestra un código QR y una dirección como <code>http://192.168.1.23:7788/</code>, y <strong>Espectadores</strong> indica cuántos dispositivos tienen la página abierta. La primera vez, puede que Windows pregunte si Sokuji puede usar la red: permítelo en las redes privadas. Si tu ordenador tiene más de una conexión de red, aparece una lista <strong>Red</strong>; elige la red en la que están tus espectadores.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "Los espectadores abren la página",
      "content": "Las personas que están en la misma red escanean el código QR con la cámara del móvil o escriben la dirección en un navegador. <strong>Copiar</strong> copia la dirección para que puedas enviarla por chat. Eligen qué quieren leer (el idioma original, la traducción o ambos) y tocan <strong>Empezar a leer</strong>. Quien se une más tarde también recibe lo que se ha dicho antes.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "Leer en un ordenador",
      "content": "En una pantalla ancha, al elegir <strong>Ambos idiomas</strong> los dos idiomas se muestran uno al lado del otro, con la hora de cada línea. Vuelve a hacer clic en el botón seleccionado para cambiar qué idioma va primero. Teclas: <code>+</code> y <code>−</code> cambian el tamaño de fuente, <code>B</code> activa o desactiva la vista de ambos idiomas, <code>F</code> pasa a pantalla completa y <code>End</code> vuelve a la línea más reciente.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "Cada espectador ajusta su propia página",
      "content": "En <strong>Visualización</strong> (el botón <strong>⋯</strong> en un ordenador), cada espectador puede cambiar el tamaño de fuente, elegir los colores <strong>Claro</strong> o <strong>Alto contraste</strong>, ocultar con <strong>Solo frases completas</strong> la frase que aún se está diciendo y usar <strong>Mantener la pantalla encendida</strong>. Estas opciones solo se aplican a ese dispositivo. Si activas <strong>Permitir que los espectadores guarden estos subtítulos</strong> en el panel para compartir, los espectadores también ven la opción <strong>Guardar estos subtítulos</strong>, que descarga los subtítulos de su página como archivo .txt.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "Muestra el código QR en una pantalla grande",
      "content": "Haz clic en <strong>Abrir la página de proyección</strong>. Se abre una ventana aparte con el código QR y la dirección en letra grande; arrástrala a un proyector o a una segunda pantalla y ponla en pantalla completa. Para ayudar a los invitados a conectarse primero a la red, activa <strong>Mostrar los datos del Wi‑Fi en la página de proyección</strong> y rellena <strong>Nombre del Wi‑Fi</strong> y la contraseña. La página muestra entonces esos datos, junto con un segundo código QR para conectarse al Wi‑Fi. Sokuji solo usa estos datos en esta página y no los guarda.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "Deja de compartir",
      "content": "Haz clic en <strong>Dejar de compartir</strong> o sal de Sokuji. En las páginas de los espectadores aparece <strong>Se ha dejado de compartir</strong>. Mientras compartes, sus páginas siguen tu conversación: cuando la borras o inicias una sesión nueva, sus páginas vuelven a empezar desde cero. Cada vez que Sokuji se inicia, la opción de compartir está desactivada."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Todos tienen que estar en la misma red que tu ordenador. Un móvil que usa datos móviles no puede abrir la página.",
      "Las redes Wi‑Fi de invitados y algunas redes de empresa impiden que los dispositivos se comuniquen entre sí. Si nadie puede conectarse, usa el punto de acceso de un móvil o tu propio router. Sokuji te avisa cuando pasan dos minutos sin que se conecte ningún dispositivo.",
      "¿Chrome en Android no la abre? Permítelo en Ajustes → Aplicaciones → Chrome → Permisos → Dispositivos cercanos. La página de proyección también muestra este aviso.",
      "La dirección empieza por http://, así que el navegador puede indicar que la página «no es segura». Es una dirección interna de tu propia red.",
      "La página no tiene contraseña: cualquiera que esté en la red y tenga la dirección puede leer los subtítulos. Deja de compartir cuando la conversación no sea para todas las personas de esa red.",
      "Si tu ordenador cambia de red, la dirección cambia. Sokuji te avisa; envía la nueva dirección o abre otra vez la página de proyección.",
      "Cuando también traduces a la otra parte de una llamada, cada línea lleva la etiqueta Presencial o En línea. Cuando la otra parte tiene dos o más hablantes (Soniox, con tu propia clave o a través de KizunaAI), sus líneas llevan la etiqueta Hablante 1, Hablante 2, etc.",
      "En una conversación cara a cara, la otra persona puede seguirla en su propio móvil en lugar de en tu pantalla."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Los espectadores necesitan Sokuji o una cuenta?",
        "answer": "No. Basta con un navegador actualizado en un móvil, una tableta o un ordenador."
      },
      {
        "question": "¿Funciona a través de internet?",
        "answer": "No. Los espectadores tienen que estar en el mismo Wi‑Fi o en la misma red por cable que tu ordenador."
      },
      {
        "question": "¿Los subtítulos pasan por los servidores de Kizuna AI?",
        "answer": "No. Sokuji sirve la página desde tu propio ordenador, y los subtítulos van directamente a los dispositivos de los espectadores a través de tu red. La traducción en sí sigue viniendo del proveedor que uses, como en cualquier sesión."
      },
      {
        "question": "¿Compartir cuesta más?",
        "answer": "No. Los espectadores leen los subtítulos que tu sesión ya genera, sean cuantos sean."
      },
      {
        "question": "¿Los espectadores pueden oír la traducción?",
        "answer": "No. La página solo muestra texto."
      },
      {
        "question": "¿Está en la extensión de navegador?",
        "answer": "No. La opción de compartir subtítulos está en la aplicación de escritorio para Windows, macOS y Linux."
      },
      {
        "question": "¿Por qué ha dejado de funcionar la dirección?",
        "answer": "La dirección corresponde a tu ordenador en la red a la que está conectado en ese momento. Cambia cuando cambias de red, y la opción de compartir vuelve a estar desactivada cada vez que se inicia Sokuji. Empieza a compartir de nuevo y envía la nueva dirección."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Partager les sous-titres en temps réel sur votre réseau",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "L’application de bureau Sokuji peut partager ses sous-titres et ses traductions en temps réel avec les personnes connectées au même Wi‑Fi ou au même réseau de bureau. Elles scannent un code QR ou saisissent une adresse, puis lisent au fur et à mesure sur leur propre téléphone ou ordinateur, dans la langue de leur choix. Il n’y a rien à installer, et aucun compte n’est nécessaire."
  },
  "steps": [
    {
      "title": "Ouvrez le panneau de partage",
      "content": "Dans l’application de bureau, cliquez sur le bouton de diffusion dans la barre d’outils au-dessus de la conversation (au survol, il affiche <strong>Partager les sous-titres</strong>). Rien n’est partagé tant que vous n’avez pas activé le partage.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "Activez le partage",
      "content": "Activez <strong>Partager les sous-titres</strong>. Le panneau affiche un code QR et une adresse du type <code>http://192.168.1.23:7788/</code>, et <strong>Spectateurs</strong> indique le nombre d’appareils sur lesquels la page est ouverte. La première fois, Windows peut demander si Sokuji a le droit d’utiliser le réseau : autorisez-le sur les réseaux privés. Si votre ordinateur a plusieurs connexions réseau, une liste <strong>Réseau</strong> apparaît ; choisissez le réseau sur lequel se trouvent vos spectateurs.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "Les spectateurs ouvrent la page",
      "content": "Les personnes connectées au même réseau scannent le code QR avec l’appareil photo de leur téléphone, ou saisissent l’adresse dans un navigateur. <strong>Copier</strong> copie l’adresse pour que vous puissiez l’envoyer par messagerie. Elles choisissent ce qu’elles veulent lire (la langue d’origine, la traduction ou les deux), puis appuient sur <strong>Commencer la lecture</strong>. Une personne qui arrive en retard reçoit aussi ce qui a été dit avant.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "Lire sur un ordinateur",
      "content": "Sur un écran large, choisir <strong>Les deux langues</strong> place les deux langues côte à côte, avec l’heure de chaque ligne. Cliquez de nouveau sur le bouton sélectionné pour changer la langue affichée en premier. Touches : <code>+</code> et <code>−</code> modifient la taille du texte, <code>B</code> active ou désactive l’affichage des deux langues, <code>F</code> passe en plein écran et <code>End</code> revient à la ligne la plus récente.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "Chaque spectateur règle sa propre page",
      "content": "Sous <strong>Affichage</strong> (le bouton <strong>⋯</strong> sur un ordinateur), chaque spectateur peut modifier la taille du texte, choisir les couleurs <strong>Clair</strong> ou <strong>Contraste élevé</strong>, masquer avec <strong>Phrases complètes uniquement</strong> la phrase qui est encore en train d’être dite et utiliser <strong>Garder l'écran allumé</strong>. Ces choix ne s’appliquent qu’à cet appareil. Si vous activez <strong>Autoriser les spectateurs à enregistrer ces sous-titres</strong> dans le panneau de partage, les spectateurs voient aussi l’option <strong>Enregistrer ces sous-titres</strong>, qui télécharge les sous-titres de leur page dans un fichier .txt.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "Affichez le code QR sur un grand écran",
      "content": "Cliquez sur <strong>Ouvrir la page de projection</strong>. Une fenêtre séparée s’ouvre avec le code QR et l’adresse en grands caractères ; faites-la glisser vers un projecteur ou un second écran et passez-la en plein écran. Pour aider d’abord les invités à se connecter au réseau, activez <strong>Afficher les infos Wi‑Fi sur la page de projection</strong>, puis renseignez <strong>Nom du Wi‑Fi</strong> et le mot de passe. La page affiche alors ces informations, avec un second code QR qui permet de rejoindre le Wi‑Fi. Sokuji n’utilise ces informations que sur cette page et ne les enregistre pas.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "Arrêtez le partage",
      "content": "Cliquez sur <strong>Arrêter le partage</strong>, ou quittez Sokuji. Les pages des spectateurs affichent <strong>Le partage est terminé</strong>. Tant que le partage est actif, leurs pages suivent votre conversation : quand vous l’effacez ou démarrez une nouvelle session, leurs pages repartent de zéro. Le partage est toujours désactivé au démarrage de Sokuji."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Tout le monde doit être sur le même réseau que votre ordinateur. Un téléphone en données mobiles ne peut pas ouvrir la page.",
      "Les Wi‑Fi invités et certains réseaux d’entreprise empêchent les appareils de communiquer entre eux. Si personne n’arrive à se connecter, utilisez le partage de connexion d’un téléphone ou votre propre routeur. Sokuji vous avertit si aucun appareil ne s’est connecté au bout de deux minutes.",
      "Chrome sur Android ne l'ouvre pas ? Autorisez-le dans Paramètres → Applications → Chrome → Autorisations → Appareils à proximité. La page de projection affiche aussi ce rappel.",
      "L’adresse commence par http:// ; un navigateur peut donc qualifier la page de « non sécurisée ». C’est une adresse interne à votre propre réseau.",
      "La page n’a pas de mot de passe : toute personne du réseau qui a l’adresse peut lire les sous-titres. Désactivez le partage quand la conversation n’est pas destinée à tous ceux qui sont sur ce réseau.",
      "Si votre ordinateur change de réseau, l’adresse change. Sokuji vous le signale ; envoyez la nouvelle adresse ou rouvrez la page de projection.",
      "Quand vous traduisez aussi ce que dit votre interlocuteur pendant un appel, chaque ligne porte la mention Sur place ou À distance. Quand il y a deux intervenants ou plus de l’autre côté (Soniox, avec votre propre clé ou via KizunaAI), leurs lignes portent la mention Intervenant 1, Intervenant 2, etc.",
      "Dans une conversation en face à face, l’autre personne peut suivre sur son propre téléphone plutôt que sur votre écran."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Les spectateurs ont-ils besoin de Sokuji ou d’un compte ?",
        "answer": "Non. Un navigateur récent sur un téléphone, une tablette ou un ordinateur suffit."
      },
      {
        "question": "Est-ce que cela fonctionne par Internet ?",
        "answer": "Non. Les spectateurs doivent être sur le même Wi‑Fi ou le même réseau filaire que votre ordinateur."
      },
      {
        "question": "Les sous-titres passent-ils par les serveurs de Kizuna AI ?",
        "answer": "Non. Sokuji sert la page depuis votre propre ordinateur, et les sous-titres vont directement aux appareils des spectateurs par votre réseau. La traduction elle-même provient toujours du fournisseur que vous utilisez, comme pour n’importe quelle session."
      },
      {
        "question": "Le partage coûte-t-il plus cher ?",
        "answer": "Non. Les spectateurs lisent les sous-titres que votre session produit déjà, quel que soit leur nombre."
      },
      {
        "question": "Les spectateurs peuvent-ils entendre la traduction ?",
        "answer": "Non. La page n’affiche que du texte."
      },
      {
        "question": "Est-ce disponible dans l’extension de navigateur ?",
        "answer": "Non. Le partage des sous-titres se trouve dans l’application de bureau pour Windows, macOS et Linux."
      },
      {
        "question": "Pourquoi l’adresse ne fonctionne-t-elle plus ?",
        "answer": "L’adresse est celle de votre ordinateur sur son réseau actuel. Elle change quand vous changez de réseau, et le partage est de nouveau désactivé à chaque démarrage de Sokuji. Réactivez le partage et communiquez la nouvelle adresse."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "مشاركة الترجمة النصية المباشرة على شبكتك",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "يستطيع تطبيق Sokuji لسطح المكتب مشاركة الترجمة النصية المباشرة (النص الأصلي والترجمة) مع الأشخاص المتصلين بشبكة Wi‑Fi نفسها أو بشبكة المكتب نفسها. يمسحون رمز QR أو يكتبون عنوانًا، ثم يتابعون القراءة على هواتفهم أو حواسيبهم المحمولة باللغة التي يختارونها. لا حاجة إلى تثبيت أي شيء ولا إلى تسجيل الدخول."
  },
  "steps": [
    {
      "title": "افتح لوحة المشاركة",
      "content": "في تطبيق سطح المكتب، انقر على زر البث في شريط الأدوات أعلى المحادثة (يظهر عند تمرير المؤشر فوقه التلميح <strong>مشاركة الترجمة</strong>). لا يُشارَك أي شيء قبل أن تفعّل المشاركة.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "فعّل المشاركة",
      "content": "فعّل خيار <strong>مشاركة الترجمة</strong>. تعرض اللوحة رمز QR وعنوانًا مثل <code dir=\"ltr\">http://192.168.1.23:7788/</code>، ويظهر بجانب <strong>المشاهدون</strong> عدد الأجهزة التي تفتح الصفحة. في المرة الأولى، قد يسألك Windows هل تسمح لـ Sokuji باستخدام الشبكة: اسمح له على الشبكات الخاصة. إذا كان لحاسوبك أكثر من اتصال بالشبكة، تظهر قائمة <strong>الشبكة</strong>؛ اختر منها الشبكة التي يتصل بها المشاهدون.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "المشاهدون يفتحون الصفحة",
      "content": "يمسح الأشخاص المتصلون بالشبكة نفسها رمز QR بكاميرا الهاتف، أو يكتبون العنوان في المتصفح. انقر على <strong>نسخ</strong> لنسخ العنوان إذا أردت إرساله عبر الدردشة. يختار المشاهدون ما يريدون قراءته (اللغة الأصلية أو الترجمة أو كلتيهما) ثم يضغطون على <strong>ابدأ القراءة</strong>. ومن ينضم متأخرًا يحصل أيضًا على ما قيل قبل انضمامه.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "القراءة على الحاسوب المحمول",
      "content": "على الشاشة العريضة، عند اختيار <strong>اللغتان معًا</strong> تظهر اللغتان جنبًا إلى جنب، مع وقت كل سطر. انقر على الزر المحدد مرة أخرى لتبديل اللغة التي تظهر أولًا. المفاتيح: <code dir=\"ltr\">+</code> و <code dir=\"ltr\">−</code> لتغيير حجم النص، و <code dir=\"ltr\">B</code> لتشغيل عرض اللغتين معًا أو إيقافه، و <code dir=\"ltr\">F</code> لملء الشاشة، و <code dir=\"ltr\">End</code> للعودة إلى أحدث سطر.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "المشاهدون يضبطون صفحاتهم",
      "content": "ضمن <strong>إعدادات العرض</strong> (زر <strong>⋯</strong> على الحاسوب المحمول)، يستطيع كل مشاهد تغيير حجم النص، واختيار نمط الألوان <strong>فاتح</strong> أو <strong>تباين عالٍ</strong>، وإخفاء الجملة التي لم يكتمل نطقها بعد بخيار <strong>الجمل المكتملة فقط</strong>، واستخدام خيار <strong>إبقاء الشاشة مضاءة</strong>. تنطبق هذه الاختيارات على ذلك الجهاز فقط. إذا فعّلت خيار <strong>السماح للمشاهدين بحفظ الترجمة</strong> في لوحة المشاركة، يظهر للمشاهدين أيضًا خيار <strong>حفظ هذه الترجمة</strong>، وهو ينزّل الترجمة النصية المعروضة في صفحتهم كملف .txt.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "اعرض رمز QR على شاشة كبيرة",
      "content": "انقر على <strong>فتح صفحة العرض</strong>. تُفتح نافذة مستقلة تعرض رمز QR والعنوان بخط كبير؛ اسحبها إلى جهاز العرض أو إلى شاشة ثانية واجعلها بملء الشاشة. لمساعدة الضيوف على الاتصال بالشبكة أولًا، فعّل خيار <strong>إظهار معلومات Wi‑Fi في صفحة العرض</strong> وأدخل <strong>اسم شبكة Wi‑Fi</strong> وكلمة المرور. عندها تعرضهما الصفحة، ومعهما رمز QR ثانٍ للانضمام إلى شبكة Wi‑Fi. يستخدم Sokuji هذه البيانات في هذه الصفحة فقط ولا يحفظها.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "أوقف المشاركة",
      "content": "انقر على <strong>إيقاف المشاركة</strong>، أو أغلق تطبيق Sokuji. تعرض صفحات المشاهدين عبارة <strong>انتهت المشاركة</strong>. ما دامت المشاركة مفعّلة، تتبع صفحاتهم محادثتك: عندما تمسحها أو تبدأ جلسة جديدة، تبدأ صفحاتهم من جديد فارغة. تكون المشاركة متوقفة دائمًا عند تشغيل Sokuji."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "يجب أن يكون الجميع على الشبكة نفسها التي يتصل بها حاسوبك. الهاتف الذي يستخدم بيانات الجوال لا يستطيع فتح الصفحة.",
      "شبكات Wi‑Fi المخصصة للضيوف وبعض شبكات الشركات تمنع الأجهزة من الوصول إلى بعضها. إذا لم يتمكن أحد من الاتصال، استخدم نقطة اتصال من هاتف أو جهاز توجيه (راوتر) خاصًا بك. ينبّهك Sokuji إذا مرّت دقيقتان ولم يتصل أي جهاز.",
      "لا يفتحها Chrome على Android؟ اسمح بذلك من الإعدادات ← التطبيقات ← Chrome ← الأذونات ← الأجهزة المجاورة. يظهر هذا التذكير في صفحة العرض أيضًا.",
      "يبدأ العنوان بـ ⁦http://⁩، لذلك قد يصف المتصفح الصفحة بأنها «غير آمنة». هذا عنوان داخل شبكتك أنت.",
      "الصفحة بلا كلمة مرور: كل من على الشبكة ويعرف العنوان يستطيع قراءة الترجمة النصية. أوقف المشاركة عندما لا تكون المحادثة موجّهة لكل من على تلك الشبكة.",
      "إذا انتقل حاسوبك إلى شبكة أخرى، يتغيّر العنوان. يخبرك Sokuji بذلك؛ أرسل العنوان الجديد أو افتح صفحة العرض من جديد.",
      "عندما تترجم أيضًا كلام الطرف الآخر في مكالمة، يحمل كل سطر علامة حضوري أو عن بُعد. وعندما يكون في الطرف الآخر متحدثان أو أكثر (Soniox، بمفتاحك الخاص أو عبر KizunaAI)، تحمل أسطرهم علامات المتحدث 1 والمتحدث 2 وهكذا.",
      "في المحادثة وجهًا لوجه، يستطيع الشخص الآخر المتابعة على هاتفه بدلًا من شاشتك."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "هل يحتاج المشاهدون إلى Sokuji أو إلى حساب؟",
        "answer": "لا. يكفي متصفح حديث على هاتف أو جهاز لوحي أو حاسوب."
      },
      {
        "question": "هل تعمل المشاركة عبر الإنترنت؟",
        "answer": "لا. يجب أن يكون المشاهدون على شبكة Wi‑Fi أو الشبكة السلكية نفسها التي يتصل بها حاسوبك."
      },
      {
        "question": "هل تمر الترجمة النصية عبر خوادم Kizuna AI؟",
        "answer": "لا. يقدّم Sokuji الصفحة من حاسوبك أنت، وتنتقل الترجمة النصية مباشرةً إلى أجهزة المشاهدين عبر شبكتك. أما عملية الترجمة نفسها فما زالت تتم لدى مزوّد الخدمة الذي تستخدمه، كما في أي جلسة."
      },
      {
        "question": "هل تزيد المشاركة التكلفة؟",
        "answer": "لا. يقرأ المشاهدون الترجمة النصية التي تنتجها جلستك أصلًا، مهما كان عددهم."
      },
      {
        "question": "هل يستطيع المشاهدون سماع الترجمة؟",
        "answer": "لا. تعرض الصفحة نصًا فقط."
      },
      {
        "question": "هل هي متاحة في امتداد المتصفح؟",
        "answer": "لا. مشاركة الترجمة النصية متاحة في تطبيق سطح المكتب لأنظمة Windows و macOS و Linux."
      },
      {
        "question": "لماذا توقّف العنوان عن العمل؟",
        "answer": "العنوان خاص بحاسوبك على شبكته الحالية. يتغيّر عندما تنتقل إلى شبكة أخرى، وتعود المشاركة متوقفة في كل مرة يُشغَّل فيها Sokuji. فعّل المشاركة وشارك العنوان الجديد."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Compartilhar legendas em tempo real na sua rede",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "O aplicativo de desktop do Sokuji pode compartilhar as legendas e traduções em tempo real com as pessoas que estão na mesma rede Wi‑Fi ou na mesma rede do escritório. Elas escaneiam um QR code ou digitam um endereço e vão lendo no próprio celular ou computador, no idioma que escolherem. Não é preciso instalar nada nem fazer login."
  },
  "steps": [
    {
      "title": "Abra o painel de compartilhamento",
      "content": "No aplicativo de desktop, clique no botão de transmissão na barra de ferramentas acima da conversa (ao passar o mouse sobre ele, aparece <strong>Compartilhar legendas</strong>). Nada é compartilhado até você ativar o compartilhamento.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "Ative o compartilhamento",
      "content": "Ative <strong>Compartilhar legendas</strong>. O painel mostra um QR code e um endereço como <code>http://192.168.1.23:7788/</code>, e <strong>Assistindo</strong> mostra quantos dispositivos estão com a página aberta. Na primeira vez, o Windows pode perguntar se o Sokuji pode usar a rede: permita em redes privadas. Se o seu computador tiver mais de uma conexão de rede, aparece uma lista <strong>Rede</strong>; escolha a rede em que os espectadores estão.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "Os espectadores abrem a página",
      "content": "As pessoas que estão na mesma rede escaneiam o QR code com a câmera do celular ou digitam o endereço em um navegador. <strong>Copiar</strong> copia o endereço para você enviar em um chat. Elas escolhem o que querem ler (o idioma original, a tradução ou os dois) e tocam em <strong>Começar a ler</strong>. Quem entra depois também recebe o que foi dito antes.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "Ler em um computador",
      "content": "Em uma tela larga, escolher <strong>Os dois idiomas</strong> coloca os dois idiomas lado a lado, com o horário de cada linha. Clique de novo no botão selecionado para trocar o idioma que vem primeiro. Teclas: <code>+</code> e <code>−</code> mudam o tamanho do texto, <code>B</code> ativa ou desativa a exibição dos dois idiomas, <code>F</code> entra em tela cheia e <code>End</code> volta para a linha mais recente.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "Cada espectador ajusta a própria página",
      "content": "Em <strong>Exibição</strong> (o botão <strong>⋯</strong> em um computador), cada espectador pode mudar o tamanho do texto, escolher as cores <strong>Claro</strong> ou <strong>Alto contraste</strong>, ocultar com <strong>Só frases completas</strong> a frase que ainda está sendo dita e usar <strong>Manter a tela ligada</strong>. Essas opções valem só para aquele dispositivo. Se você ativar <strong>Permitir que os espectadores salvem estas legendas</strong> no painel de compartilhamento, os espectadores também passam a ver a opção <strong>Salvar estas legendas</strong>, que baixa as legendas da página deles como arquivo .txt.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "Mostre o QR code em uma tela grande",
      "content": "Clique em <strong>Abrir página de projeção</strong>. Uma janela separada se abre com o QR code e o endereço em letras grandes; arraste-a para um projetor ou uma segunda tela e coloque-a em tela cheia. Para ajudar os convidados a se conectarem à rede antes, ative <strong>Mostrar os dados do Wi‑Fi na página de projeção</strong> e preencha <strong>Nome do Wi‑Fi</strong> e a senha. A página passa a mostrar esses dados, com um segundo QR code para entrar no Wi‑Fi. O Sokuji usa esses dados só nessa página e não os salva.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "Pare de compartilhar",
      "content": "Clique em <strong>Parar de compartilhar</strong> ou saia do Sokuji. As páginas dos espectadores mostram <strong>O compartilhamento terminou</strong>. Enquanto o compartilhamento está ativo, as páginas deles acompanham a sua conversa: quando você a limpa ou inicia uma nova sessão, as páginas deles recomeçam do zero. O compartilhamento está sempre desativado quando o Sokuji é iniciado."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Todos precisam estar na mesma rede que o seu computador. Um celular usando dados móveis não consegue abrir a página.",
      "Redes Wi‑Fi de convidados e algumas redes corporativas impedem que os dispositivos se comuniquem entre si. Se ninguém conseguir se conectar, use o hotspot de um celular ou o seu próprio roteador. O Sokuji avisa quando nenhum dispositivo se conecta depois de dois minutos.",
      "O Chrome no Android não abre? Permita em Configurações → Apps → Chrome → Permissões → Dispositivos por perto. A página de projeção também mostra esse lembrete.",
      "O endereço começa com http://, então o navegador pode dizer que a página é “não segura”. É um endereço interno da sua própria rede.",
      "A página não tem senha: qualquer pessoa na rede que tenha o endereço pode ler as legendas. Desative o compartilhamento quando a conversa não for para todos que estão nessa rede.",
      "Se o seu computador mudar de rede, o endereço muda. O Sokuji avisa você; envie o novo endereço ou abra a página de projeção de novo.",
      "Quando você também traduz o outro lado de uma chamada, cada linha é marcada como Presencial ou Remoto. Quando o outro lado tem dois ou mais falantes (Soniox, com a sua própria chave ou pela KizunaAI), as linhas deles são marcadas como Falante 1, Falante 2 e assim por diante.",
      "Em uma conversa cara a cara, a outra pessoa pode acompanhar no próprio celular em vez de na sua tela."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Os espectadores precisam do Sokuji ou de uma conta?",
        "answer": "Não. Basta um navegador atualizado em um celular, tablet ou computador."
      },
      {
        "question": "Funciona pela internet?",
        "answer": "Não. Os espectadores precisam estar na mesma rede Wi‑Fi ou cabeada que o seu computador."
      },
      {
        "question": "As legendas passam pelos servidores da Kizuna AI?",
        "answer": "Não. O Sokuji serve a página a partir do seu próprio computador, e as legendas vão direto para os dispositivos dos espectadores pela sua rede. A tradução em si continua vindo do provedor que você usa, como em qualquer sessão."
      },
      {
        "question": "Compartilhar custa mais?",
        "answer": "Não. Os espectadores leem as legendas que a sua sessão já produz, não importa quantos sejam."
      },
      {
        "question": "Os espectadores conseguem ouvir a tradução?",
        "answer": "Não. A página mostra apenas texto."
      },
      {
        "question": "Está disponível na extensão de navegador?",
        "answer": "Não. O compartilhamento de legendas fica no aplicativo de desktop para Windows, macOS e Linux."
      },
      {
        "question": "Por que o endereço parou de funcionar?",
        "answer": "O endereço pertence ao seu computador na rede em que ele está no momento. Ele muda quando você troca de rede, e o compartilhamento volta a ficar desativado toda vez que o Sokuji é iniciado. Ative o compartilhamento e envie o novo endereço."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Показ субтитров в реальном времени по локальной сети",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Настольное приложение Sokuji может показывать субтитры и переводы в реальном времени людям в той же сети Wi‑Fi или офисной сети. Они сканируют QR-код или вводят адрес и читают на своём телефоне или ноутбуке на выбранном языке. Ничего устанавливать не нужно, входить в учётную запись тоже."
  },
  "steps": [
    {
      "title": "Откройте панель показа",
      "content": "В настольном приложении нажмите кнопку трансляции на панели инструментов над разговором (при наведении появляется подсказка <strong>Поделиться субтитрами</strong>). Пока вы не включите показ, ничего не передаётся.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "Включите показ",
      "content": "Включите <strong>Поделиться субтитрами</strong>. На панели появятся QR-код и адрес вида <code>http://192.168.1.23:7788/</code>, а счётчик <strong>Смотрят</strong> показывает, на скольких устройствах открыта страница. В первый раз Windows может спросить, разрешить ли Sokuji доступ к сети: разрешите доступ для частных сетей. Если у компьютера несколько сетевых подключений, появится список <strong>Сеть</strong>; выберите ту сеть, в которой находятся ваши зрители.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "Зрители открывают страницу",
      "content": "Люди в той же сети сканируют QR-код камерой телефона или вводят адрес в браузере. Кнопка <strong>Копировать</strong> копирует адрес, чтобы вы могли отправить его в чат. Зрители выбирают, что читать (оригинал, перевод или оба языка), и нажимают <strong>Начать чтение</strong>. Тот, кто подключится позже, увидит и то, что было сказано раньше.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "Чтение на ноутбуке",
      "content": "На широком экране при выборе <strong>Оба языка</strong> два языка располагаются рядом, и у каждой строки указано время. Нажмите выбранную кнопку ещё раз, чтобы поменять языки местами. Клавиши: <code>+</code> и <code>−</code> меняют размер текста, <code>B</code> включает и выключает двуязычный режим, <code>F</code> открывает полноэкранный режим, а <code>End</code> возвращает к последней строке.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "Зрители настраивают свою страницу",
      "content": "В разделе <strong>Отображение</strong> (на ноутбуке — кнопка <strong>⋯</strong>) каждый зритель может изменить размер текста, выбрать оформление <strong>Светлое</strong> или <strong>Высокий контраст</strong>, скрыть фразу, которая ещё произносится, с помощью параметра <strong>Только законченные фразы</strong> и включить параметр <strong>Не выключать экран</strong>. Эти настройки действуют только на этом устройстве. Если вы включите на панели показа <strong>Разрешить зрителям сохранять эти субтитры</strong>, у зрителей появится ещё и пункт <strong>Сохранить эти субтитры</strong>: он скачивает субтитры с их страницы в виде файла .txt.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "Покажите QR-код на большом экране",
      "content": "Нажмите <strong>Открыть страницу для проектора</strong>. Откроется отдельное окно с QR-кодом и адресом крупным шрифтом; перетащите его на проектор или второй экран и разверните на весь экран. Чтобы гости сначала могли подключиться к сети, включите <strong>Показывать данные Wi‑Fi на странице для проектора</strong>, заполните поле <strong>Название Wi‑Fi</strong> и укажите пароль. Тогда страница покажет эти данные и второй QR-код для подключения к Wi‑Fi. Sokuji использует эти данные только на этой странице и не сохраняет их.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "Остановите показ",
      "content": "Нажмите <strong>Остановить показ</strong> или выйдите из Sokuji. На страницах зрителей появится надпись <strong>Показ завершён</strong>. Пока показ включён, страницы зрителей отражают ваш разговор: когда вы очищаете его или начинаете новый сеанс, их страницы тоже очищаются и начинаются заново. При запуске Sokuji показ всегда выключен."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Все должны быть в той же сети, что и ваш компьютер. Телефон, подключённый через мобильный интернет, страницу не откроет.",
      "Гостевые сети Wi‑Fi и некоторые корпоративные сети не дают устройствам связываться друг с другом. Если никто не может подключиться, используйте точку доступа на телефоне или собственный роутер. Sokuji предупредит вас, если за две минуты не подключилось ни одно устройство.",
      "Chrome на Android не открывает страницу? Разрешите в Настройки → Приложения → Chrome → Разрешения → Устройства поблизости. Это напоминание есть и на странице для проектора.",
      "Адрес начинается с http://, поэтому браузер может назвать страницу «небезопасной». Это адрес внутри вашей собственной сети.",
      "Страница не защищена паролем: субтитры может читать любой человек в сети, у которого есть адрес. Выключайте показ, если разговор не предназначен для всех в этой сети.",
      "Если компьютер переходит в другую сеть, адрес меняется. Sokuji сообщит вам об этом; отправьте новый адрес или снова откройте страницу для проектора.",
      "Если вы переводите ещё и собеседника по звонку, каждая строка получает пометку Очно или Онлайн. Когда на стороне собеседника двое или более говорящих (Soniox — с вашим ключом или через KizunaAI), их строки помечаются Говорящий 1, Говорящий 2 и так далее.",
      "При разговоре лицом к лицу собеседник может следить за разговором на своём телефоне, а не на вашем экране."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Нужны ли зрителям Sokuji или учётная запись?",
        "answer": "Нет. Достаточно современного браузера на телефоне, планшете или компьютере."
      },
      {
        "question": "Работает ли это через интернет?",
        "answer": "Нет. Зрители должны быть в той же сети Wi‑Fi или проводной сети, что и ваш компьютер."
      },
      {
        "question": "Проходят ли субтитры через серверы Kizuna AI?",
        "answer": "Нет. Sokuji размещает страницу на вашем собственном компьютере, и субтитры идут по вашей сети прямо на устройства зрителей. Сам перевод по-прежнему выполняет выбранный вами провайдер, как в любом сеансе."
      },
      {
        "question": "Нужно ли платить за показ дополнительно?",
        "answer": "Нет. Сколько бы ни было зрителей, они читают субтитры, которые ваш сеанс и так создаёт."
      },
      {
        "question": "Могут ли зрители слышать перевод?",
        "answer": "Нет. Страница показывает только текст."
      },
      {
        "question": "Есть ли это в расширении для браузера?",
        "answer": "Нет. Показ субтитров есть в настольном приложении для Windows, macOS и Linux."
      },
      {
        "question": "Почему адрес перестал открываться?",
        "answer": "Адрес принадлежит вашему компьютеру в его текущей сети. Он меняется, когда вы переходите в другую сеть, а при каждом запуске Sokuji показ снова выключен. Включите показ и отправьте зрителям новый адрес."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Live-Untertitel im eigenen Netzwerk teilen",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Die Desktop-App von Sokuji kann ihre Live-Untertitel und Übersetzungen mit Personen im selben Wi‑Fi oder Büronetzwerk teilen. Wer mitlesen möchte, scannt einen QR-Code oder gibt eine Adresse ein und liest auf dem eigenen Smartphone oder Laptop in der gewünschten Sprache mit. Es muss nichts installiert werden, und eine Anmeldung ist nicht nötig."
  },
  "steps": [
    {
      "title": "Den Bereich zum Teilen öffnen",
      "content": "Klicken Sie in der Desktop-App in der Symbolleiste über der Unterhaltung auf die Cast-Schaltfläche (ihr Tooltip lautet <strong>Untertitel teilen</strong>). Solange Sie das Teilen nicht einschalten, wird nichts geteilt.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "Das Teilen einschalten",
      "content": "Schalten Sie <strong>Untertitel teilen</strong> ein. Der Bereich zeigt einen QR-Code und eine Adresse wie <code>http://192.168.1.23:7788/</code>; bei <strong>Zuschauer</strong> sehen Sie, auf wie vielen Geräten die Seite geöffnet ist. Beim ersten Mal fragt Windows eventuell, ob Sokuji das Netzwerk nutzen darf: Erlauben Sie den Zugriff für private Netzwerke. Hat Ihr Computer mehr als eine Netzwerkverbindung, erscheint die Liste <strong>Netzwerk</strong>; wählen Sie das Netzwerk, in dem sich Ihre Zuschauer befinden.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "Zuschauer öffnen die Seite",
      "content": "Personen im selben Netzwerk scannen den QR-Code mit der Kamera ihres Smartphones oder geben die Adresse in einen Browser ein. Mit <strong>Kopieren</strong> kopieren Sie die Adresse, etwa um sie in einem Chat zu senden. Die Zuschauer wählen, was sie lesen möchten (die Originalsprache, die Übersetzung oder beides), und tippen auf <strong>Lesen starten</strong>. Wer später dazukommt, sieht auch, was vorher gesagt wurde.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "Am Laptop lesen",
      "content": "Auf einem breiten Bildschirm stehen die beiden Sprachen mit <strong>Beide Sprachen</strong> nebeneinander, jede Zeile mit ihrer Uhrzeit. Ein erneuter Klick auf die ausgewählte Schaltfläche tauscht die Reihenfolge der Sprachen. Tasten: <code>+</code> und <code>−</code> ändern die Schriftgröße, <code>B</code> schaltet die zweisprachige Ansicht ein oder aus, <code>F</code> wechselt in den Vollbildmodus, und <code>End</code> springt zur neuesten Zeile.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "Zuschauer passen ihre eigene Seite an",
      "content": "Unter <strong>Anzeige</strong> (am Laptop die Schaltfläche <strong>⋯</strong>) können die Zuschauer jeweils die Schriftgröße ändern, die Farben <strong>Hell</strong> oder <strong>Hoher Kontrast</strong> wählen, mit <strong>Nur vollständige Sätze</strong> den Satz ausblenden, der gerade noch gesprochen wird, und <strong>Bildschirm eingeschaltet lassen</strong> nutzen. Diese Einstellungen gelten nur für das jeweilige Gerät. Wenn Sie im Bereich zum Teilen <strong>Zuschauer dürfen diese Untertitel speichern</strong> einschalten, erhalten die Zuschauer zusätzlich <strong>Diese Untertitel speichern</strong>: Damit laden sie die Untertitel ihrer Seite als .txt-Datei herunter.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "Den QR-Code auf einem großen Bildschirm zeigen",
      "content": "Klicken Sie auf <strong>Projektionsseite öffnen</strong>. Es öffnet sich ein eigenes Fenster mit dem QR-Code und der Adresse in großer Schrift; ziehen Sie es auf einen Projektor oder einen zweiten Bildschirm und schalten Sie es auf Vollbild. Damit sich Gäste zuerst mit dem Netzwerk verbinden können, schalten Sie <strong>Wi‑Fi-Hinweis auf der Projektionsseite anzeigen</strong> ein und geben Sie bei <strong>Wi‑Fi-Name</strong> den Namen und dazu das Passwort ein. Die Seite zeigt dann beides an, dazu einen zweiten QR-Code, der das Gerät mit dem Wi‑Fi verbindet. Sokuji verwendet diese Angaben nur auf dieser Seite und speichert sie nicht.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "Das Teilen beenden",
      "content": "Klicken Sie auf <strong>Teilen beenden</strong> oder beenden Sie Sokuji. Auf den Seiten der Zuschauer steht dann <strong>Das Teilen wurde beendet</strong>. Solange das Teilen eingeschaltet ist, folgen die Seiten der Zuschauer Ihrer Unterhaltung: Wenn Sie die Unterhaltung löschen oder eine neue Sitzung starten, beginnen auch diese Seiten wieder leer. Beim Start von Sokuji ist das Teilen immer ausgeschaltet."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Alle müssen im selben Netzwerk sein wie Ihr Computer. Ein Smartphone, das über mobile Daten online ist, kann die Seite nicht öffnen.",
      "Gast-Wi‑Fi und manche Firmennetzwerke lassen keine Verbindungen zwischen Geräten zu. Wenn sich niemand verbinden kann, nutzen Sie einen Handy-Hotspot oder Ihren eigenen Router. Sokuji warnt Sie, wenn sich nach zwei Minuten noch kein Gerät verbunden hat.",
      "Öffnet Chrome auf Android die Seite nicht? Unter Einstellungen → Apps → Chrome → Berechtigungen → Geräte in der Nähe erlauben. Dieser Hinweis steht auch auf der Projektionsseite.",
      "Die Adresse beginnt mit http://, deshalb bezeichnet ein Browser die Seite möglicherweise als „nicht sicher“. Es ist eine Adresse in Ihrem eigenen Netzwerk.",
      "Die Seite hat kein Passwort: Jeder im Netzwerk, der die Adresse kennt, kann die Untertitel lesen. Schalten Sie das Teilen aus, wenn die Unterhaltung nicht für alle in diesem Netzwerk bestimmt ist.",
      "Wechselt Ihr Computer das Netzwerk, ändert sich die Adresse. Sokuji weist Sie darauf hin; senden Sie die neue Adresse oder öffnen Sie die Projektionsseite erneut.",
      "Wenn Sie in einem Anruf auch Ihr Gegenüber übersetzen, ist jede Zeile mit Vor Ort oder Online gekennzeichnet. Wenn auf der anderen Seite zwei oder mehr Personen sprechen (Soniox, mit eigenem Schlüssel oder über KizunaAI), sind ihre Zeilen mit Sprecher 1, Sprecher 2 und so weiter gekennzeichnet.",
      "Bei einem Gespräch von Angesicht zu Angesicht kann die andere Person auf ihrem eigenen Smartphone mitlesen statt auf Ihrem Bildschirm."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Brauchen Zuschauer Sokuji oder ein Konto?",
        "answer": "Nein. Ein aktueller Browser auf einem Smartphone, Tablet oder Computer genügt."
      },
      {
        "question": "Funktioniert das über das Internet?",
        "answer": "Nein. Die Zuschauer müssen im selben Wi‑Fi oder kabelgebundenen Netzwerk sein wie Ihr Computer."
      },
      {
        "question": "Laufen die Untertitel über die Server von Kizuna AI?",
        "answer": "Nein. Sokuji stellt die Seite von Ihrem eigenen Computer aus bereit, und die Untertitel gehen über Ihr Netzwerk direkt an die Geräte der Zuschauer. Die Übersetzung selbst kommt weiterhin von dem Anbieter, den Sie nutzen, wie in jeder Sitzung."
      },
      {
        "question": "Kostet das Teilen mehr?",
        "answer": "Nein. Die Zuschauer lesen die Untertitel, die Ihre Sitzung ohnehin erzeugt, egal wie viele Zuschauer es sind."
      },
      {
        "question": "Können Zuschauer die Übersetzung hören?",
        "answer": "Nein. Die Seite zeigt nur Text."
      },
      {
        "question": "Gibt es das in der Browsererweiterung?",
        "answer": "Nein. Das Teilen von Untertiteln gibt es in der Desktop-App für Windows, macOS und Linux."
      },
      {
        "question": "Warum funktioniert die Adresse nicht mehr?",
        "answer": "Die Adresse gehört zu Ihrem Computer in seinem aktuellen Netzwerk. Sie ändert sich, wenn Sie das Netzwerk wechseln, und bei jedem Start von Sokuji ist das Teilen wieder ausgeschaltet. Schalten Sie das Teilen ein und geben Sie die neue Adresse weiter."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Показ субтитрів наживо в локальній мережі",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Настільний застосунок Sokuji може показувати субтитри й переклади наживо людям у тій самій мережі Wi‑Fi чи офісній мережі. Вони сканують QR-код або вводять адресу й читають на власному телефоні чи ноутбуці тією мовою, яку виберуть. Нічого встановлювати не потрібно, входити в обліковий запис теж."
  },
  "steps": [
    {
      "title": "Відкрийте панель показу",
      "content": "У настільному застосунку натисніть кнопку трансляції на панелі інструментів над розмовою (якщо навести курсор, з’являється підказка <strong>Поділитися субтитрами</strong>). Поки ви не ввімкнете показ, нічого не передається.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "Увімкніть показ",
      "content": "Увімкніть <strong>Поділитися субтитрами</strong>. На панелі з’являться QR-код і адреса на зразок <code>http://192.168.1.23:7788/</code>, а лічильник <strong>Дивляться</strong> показує, на скількох пристроях відкрито сторінку. Уперше Windows може запитати, чи дозволити Sokuji доступ до мережі: дозвольте доступ для приватних мереж. Якщо комп’ютер має кілька мережевих підключень, з’явиться список <strong>Мережа</strong>; виберіть ту мережу, у якій перебувають ваші глядачі.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "Глядачі відкривають сторінку",
      "content": "Люди в тій самій мережі сканують QR-код камерою телефона або вводять адресу в браузері. Кнопка <strong>Копіювати</strong> копіює адресу, щоб ви могли надіслати її в чат. Глядачі вибирають, що читати (оригінал, переклад або обидві мови), і натискають <strong>Почати читання</strong>. Хто приєднається пізніше, побачить і те, що було сказано раніше.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "Читання на ноутбуці",
      "content": "На широкому екрані, якщо вибрати <strong>Обидві мови</strong>, дві мови розташовуються поруч, і біля кожного рядка вказано час. Натисніть вибрану кнопку ще раз, щоб поміняти мови місцями. Клавіші: <code>+</code> і <code>−</code> змінюють розмір тексту, <code>B</code> вмикає й вимикає двомовний режим, <code>F</code> відкриває повноекранний режим, а <code>End</code> повертає до останнього рядка.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "Глядачі налаштовують власну сторінку",
      "content": "У розділі <strong>Відображення</strong> (на ноутбуці — кнопка <strong>⋯</strong>) кожен глядач може змінити розмір тексту, вибрати оформлення <strong>Світле</strong> або <strong>Висока контрастність</strong>, приховати фразу, яка ще промовляється, за допомогою параметра <strong>Лише завершені фрази</strong> та ввімкнути параметр <strong>Не вимикати екран</strong>. Ці налаштування діють лише на цьому пристрої. Якщо ви ввімкнете на панелі показу <strong>Дозволити глядачам зберігати ці субтитри</strong>, у глядачів з’явиться ще й пункт <strong>Зберегти ці субтитри</strong>: він завантажує субтитри з їхньої сторінки як файл .txt.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "Покажіть QR-код на великому екрані",
      "content": "Натисніть <strong>Відкрити сторінку для проєктора</strong>. Відкриється окреме вікно з QR-кодом і адресою великим шрифтом; перетягніть його на проєктор або другий екран і розгорніть на весь екран. Щоб гості спершу могли підключитися до мережі, увімкніть <strong>Показувати дані Wi‑Fi на сторінці для проєктора</strong>, заповніть поле <strong>Назва Wi‑Fi</strong> і вкажіть пароль. Тоді сторінка покаже ці дані та другий QR-код для підключення до Wi‑Fi. Sokuji використовує ці дані лише на цій сторінці й не зберігає їх.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "Зупиніть показ",
      "content": "Натисніть <strong>Зупинити показ</strong> або вийдіть із Sokuji. На сторінках глядачів з’явиться напис <strong>Показ завершено</strong>. Поки показ увімкнено, сторінки глядачів відображають вашу розмову: коли ви очищаєте її або починаєте новий сеанс, їхні сторінки теж очищаються й починаються заново. Під час запуску Sokuji показ завжди вимкнено."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Усі мають бути в тій самій мережі, що й ваш комп’ютер. Телефон, підключений через мобільний інтернет, сторінку не відкриє.",
      "Гостьові мережі Wi‑Fi і деякі корпоративні мережі не дають пристроям зв’язуватися між собою. Якщо ніхто не може підключитися, скористайтеся точкою доступу на телефоні або власним роутером. Sokuji попередить вас, якщо за дві хвилини не підключився жоден пристрій.",
      "Chrome на Android не відкриває сторінку? Дозвольте в Налаштування → Додатки → Chrome → Дозволи → Пристрої поблизу. Це нагадування є й на сторінці для проєктора.",
      "Адреса починається з http://, тому браузер може назвати сторінку «незахищеною». Це адреса всередині вашої власної мережі.",
      "Сторінка не захищена паролем: субтитри може читати будь-хто в мережі, хто має адресу. Вимикайте показ, якщо розмова не призначена для всіх у цій мережі.",
      "Якщо комп’ютер переходить в іншу мережу, адреса змінюється. Sokuji повідомить вас про це; надішліть нову адресу або знову відкрийте сторінку для проєктора.",
      "Якщо ви перекладаєте ще й співрозмовника під час дзвінка, кожен рядок має позначку Очно або Онлайн. Коли на боці співрозмовника двоє або більше мовців (Soniox — з вашим власним ключем або через KizunaAI), їхні рядки позначаються Мовець 1, Мовець 2 і так далі.",
      "Під час розмови віч-на-віч співрозмовник може стежити за розмовою на власному телефоні, а не на вашому екрані."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Чи потрібні глядачам Sokuji або обліковий запис?",
        "answer": "Ні. Достатньо сучасного браузера на телефоні, планшеті чи комп’ютері."
      },
      {
        "question": "Чи працює це через інтернет?",
        "answer": "Ні. Глядачі мають бути в тій самій мережі Wi‑Fi або дротовій мережі, що й ваш комп’ютер."
      },
      {
        "question": "Чи проходять субтитри через сервери Kizuna AI?",
        "answer": "Ні. Sokuji розміщує сторінку на вашому власному комп’ютері, і субтитри йдуть вашою мережею безпосередньо на пристрої глядачів. Сам переклад, як і в будь-якому сеансі, виконує вибраний вами провайдер."
      },
      {
        "question": "Чи треба доплачувати за показ?",
        "answer": "Ні. Хоч би скільки було глядачів, вони читають субтитри, які ваш сеанс створює й так."
      },
      {
        "question": "Чи можуть глядачі чути переклад?",
        "answer": "Ні. Сторінка показує лише текст."
      },
      {
        "question": "Чи є це в розширенні для браузера?",
        "answer": "Ні. Показ субтитрів є в настільному застосунку для Windows, macOS і Linux."
      },
      {
        "question": "Чому адреса перестала відкриватися?",
        "answer": "Адреса належить вашому комп’ютеру в його поточній мережі. Вона змінюється, коли ви переходите в іншу мережу, а під час кожного запуску Sokuji показ знову вимкнено. Увімкніть показ і надішліть глядачам нову адресу."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Condividere i sottotitoli in tempo reale sulla tua rete",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "L’app desktop di Sokuji può condividere i sottotitoli e le traduzioni in tempo reale con le persone collegate allo stesso Wi‑Fi o alla stessa rete dell’ufficio. Scansionano un codice QR o digitano un indirizzo e leggono man mano sul proprio telefono o computer, nella lingua che scelgono. Non c’è niente da installare e non serve alcun accesso."
  },
  "steps": [
    {
      "title": "Apri il pannello di condivisione",
      "content": "Nell’app desktop, fai clic sul pulsante di trasmissione nella barra degli strumenti sopra la conversazione (passandoci sopra con il mouse compare <strong>Condividi sottotitoli</strong>). Finché non attivi la condivisione, non viene condiviso nulla.",
      "screenshot": "01-share-panel.png"
    },
    {
      "title": "Attiva la condivisione",
      "content": "Attiva <strong>Condividi sottotitoli</strong>. Il pannello mostra un codice QR e un indirizzo come <code>http://192.168.1.23:7788/</code>, e <strong>Spettatori</strong> indica quanti dispositivi hanno la pagina aperta. La prima volta, Windows potrebbe chiedere se Sokuji può usare la rete: consentilo sulle reti private. Se il tuo computer ha più di una connessione di rete, compare un elenco <strong>Rete</strong>; scegli la rete a cui sono collegati i tuoi spettatori.",
      "screenshot": "02-sharing-on.png"
    },
    {
      "title": "Gli spettatori aprono la pagina",
      "content": "Le persone sulla stessa rete scansionano il codice QR con la fotocamera del telefono oppure digitano l’indirizzo in un browser. Con <strong>Copia</strong> copi l’indirizzo, così puoi inviarlo in una chat. Scelgono cosa leggere (la lingua originale, la traduzione o entrambe) e toccano <strong>Inizia a leggere</strong>. Chi si collega più tardi riceve anche ciò che è stato detto prima.",
      "screenshot": "03-viewer-phone.png"
    },
    {
      "title": "Leggere su un computer",
      "content": "Su uno schermo largo, scegliendo <strong>Entrambe le lingue</strong> le due lingue compaiono affiancate, con l’ora di ogni riga. Fai di nuovo clic sul pulsante selezionato per scambiare la lingua che viene per prima. Tasti: <code>+</code> e <code>−</code> cambiano la dimensione del carattere, <code>B</code> attiva o disattiva la visualizzazione di entrambe le lingue, <code>F</code> passa a schermo intero e <code>End</code> torna alla riga più recente.",
      "screenshot": "04-viewer-laptop.png"
    },
    {
      "title": "Ogni spettatore regola la propria pagina",
      "content": "In <strong>Visualizzazione</strong> (il pulsante <strong>⋯</strong> su un computer), ogni spettatore può cambiare la dimensione del carattere, scegliere i colori <strong>Chiaro</strong> o <strong>Alto contrasto</strong>, nascondere con <strong>Solo frasi complete</strong> la frase che si sta ancora pronunciando e usare <strong>Mantieni lo schermo acceso</strong>. Queste scelte valgono solo per quel dispositivo. Se attivi <strong>Consenti agli spettatori di salvare questi sottotitoli</strong> nel pannello di condivisione, gli spettatori trovano anche l’opzione <strong>Salva questi sottotitoli</strong>, che scarica i sottotitoli della loro pagina come file .txt.",
      "screenshot": "05-viewer-settings.png"
    },
    {
      "title": "Mostra il codice QR su uno schermo grande",
      "content": "Fai clic su <strong>Apri la pagina di proiezione</strong>. Si apre una finestra separata con il codice QR e l’indirizzo a caratteri grandi; trascinala su un proiettore o su un secondo schermo e mettila a schermo intero. Per aiutare prima gli ospiti a collegarsi alla rete, attiva <strong>Mostra i dati del Wi‑Fi nella pagina di proiezione</strong> e compila <strong>Nome del Wi‑Fi</strong> e la password. La pagina mostra allora questi dati, insieme a un secondo codice QR per collegarsi al Wi‑Fi. Sokuji usa questi dati solo in questa pagina e non li salva.",
      "screenshot": "06-projector-page.png"
    },
    {
      "title": "Interrompi la condivisione",
      "content": "Fai clic su <strong>Interrompi la condivisione</strong> oppure esci da Sokuji. Le pagine degli spettatori mostrano <strong>La condivisione è terminata</strong>. Finché la condivisione è attiva, le loro pagine seguono la tua conversazione: quando la cancelli o avvii una nuova sessione, le loro pagine ripartono da zero. All’avvio di Sokuji la condivisione è sempre disattivata."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Tutti devono essere sulla stessa rete del tuo computer. Un telefono che usa i dati mobili non può aprire la pagina.",
      "Le reti Wi‑Fi per ospiti e alcune reti aziendali impediscono ai dispositivi di comunicare tra loro. Se nessuno riesce a collegarsi, usa l’hotspot di un telefono o un tuo router. Sokuji ti avvisa se dopo due minuti non si è collegato nessun dispositivo.",
      "Chrome su Android non la apre? Consentilo in Impostazioni → App → Chrome → Autorizzazioni → Dispositivi nelle vicinanze. Anche la pagina di proiezione mostra questo promemoria.",
      "L’indirizzo inizia con http://, quindi il browser potrebbe segnalare la pagina come «non sicura». È un indirizzo interno alla tua rete.",
      "La pagina non ha password: chiunque sia sulla rete e abbia l’indirizzo può leggere i sottotitoli. Disattiva la condivisione quando la conversazione non è destinata a tutti quelli che sono su quella rete.",
      "Se il tuo computer cambia rete, l’indirizzo cambia. Sokuji te lo segnala; invia il nuovo indirizzo o riapri la pagina di proiezione.",
      "Quando traduci anche l’altra parte di una chiamata, ogni riga riporta l’indicazione In presenza o Da remoto. Quando dall’altra parte ci sono due o più interlocutori (Soniox, con la tua chiave o tramite KizunaAI), le loro righe riportano Interlocutore 1, Interlocutore 2 e così via.",
      "In una conversazione faccia a faccia, l’altra persona può seguire sul proprio telefono invece che sul tuo schermo."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Agli spettatori servono Sokuji o un account?",
        "answer": "No. Basta un browser aggiornato su un telefono, un tablet o un computer."
      },
      {
        "question": "Funziona via Internet?",
        "answer": "No. Gli spettatori devono essere sulla stessa rete Wi‑Fi o cablata del tuo computer."
      },
      {
        "question": "I sottotitoli passano dai server di Kizuna AI?",
        "answer": "No. Sokuji serve la pagina dal tuo computer e i sottotitoli arrivano direttamente ai dispositivi degli spettatori attraverso la tua rete. La traduzione in sé continua ad arrivare dal fornitore che usi, come in qualsiasi sessione."
      },
      {
        "question": "La condivisione costa di più?",
        "answer": "No. Gli spettatori leggono i sottotitoli che la tua sessione produce già, a prescindere da quanti sono."
      },
      {
        "question": "Gli spettatori possono ascoltare la traduzione?",
        "answer": "No. La pagina mostra solo testo."
      },
      {
        "question": "È disponibile nell’estensione per browser?",
        "answer": "No. La condivisione dei sottotitoli è nell’app desktop per Windows, macOS e Linux."
      },
      {
        "question": "Perché l’indirizzo ha smesso di funzionare?",
        "answer": "L’indirizzo è quello del tuo computer sulla rete a cui è collegato in quel momento. Cambia quando cambi rete, e la condivisione è di nuovo disattivata a ogni avvio di Sokuji. Riattiva la condivisione e invia il nuovo indirizzo."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureCaptionSharing() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/caption-sharing" />;
}
